import Errors from "../../utilities/constants/Errors";
import KeycloakService from "../config/KeycloakService";
import HttpClient from "../../utilities/functions/HttpClient";
import CustomToast from "../../utilities/components/CustomToast";
import {
  USERS_ENDPOINT,
  APPOINTMENTS_ENDPOINT,
} from "../../utilities/constants/ServerEndPoints";

/**
 * request
 * Generic function to handle API requests for appointment operations.
 * @param {Function} operation The specific operation to perform (e.g., HttpClient.get, HttpClient.post).
 * @param {Object} options Optional settings for the request, such as loading and error handlers.
 * @returns {Promise<Object>} The response data from the API.
 */
const request = async (operation, { setIsLoading, setApiError } = {}) => {
  setApiError?.(false);

  try {
    return await operation();
  } catch (err) {
    setApiError?.(true);
    handleAppointmentRequestError(err);
    throw err;
  } finally {
    setIsLoading?.(false);
  }
};

/**
 * normalizeAppointmentQueryParams
 * Normalize and map frontend appointment query parameters to backend-compatible ones.
 * @param {Object} params The original query parameters from the frontend.
 * @returns {Object} The normalized query parameters for the backend.
 */
const normalizeAppointmentQueryParams = (params = {}) => {
  const normalizedParams = { ...params };

  if (
    normalizedParams.pageSize != null &&
    normalizedParams.size == null
  ) {
    normalizedParams.size = normalizedParams.pageSize;
  }

  delete normalizedParams.pageSize;

  return normalizedParams;
};

/**
 * Fetch all appointments from the backend.
 * @param {Function} setAppointmentsData The function to collect and set the data from the database.
 * @param {Object} params The parameters to filter the data.
 * @param {Function} setIsLoading The function to update the loading status.
 * @param {Function} setApiError The function to update the api error status.
 * @returns {Promise<void>} A promise.
 * @throws {Error} An error.
 */
export const getAllAppointments = async (
  setAppointmentsData,
  params,
  setIsLoading,
  setApiError,
) => {
  const normalizedParams = normalizeAppointmentQueryParams(params);

  const response = await request(
    () => HttpClient.getAxiosClient().get(`${APPOINTMENTS_ENDPOINT}`, { params: normalizedParams }),
    { setIsLoading, setApiError },
  );

  if (response.status === 200) {
    setAppointmentsData(response.data);
  }
};

/**
 * Fetch all appointments records for a single user from the backend.
 * @param {Function} setAppointmentsData The function to collect and set the data from the database.
 * @param {string} id The user's id to fetch.
 * @param {Function} setIsLoading The function to update the loading status.
 * @param {Object} params The parameters to filter the data.
 * @returns {Promise<void>} A promise.
 * @throws {Error} An error.
 */
export const getAppointmentsByUserId = async (
  setAppointmentsData,
  id,
  setIsLoading,
  setApiError,
  params,
) => {
  const normalizedParams = normalizeAppointmentQueryParams(params);

  const response = await request(
    () =>
      HttpClient.getAxiosClient().get(`${USERS_ENDPOINT}/${id}${APPOINTMENTS_ENDPOINT}`, {
        params: normalizedParams,
      }),
    { setIsLoading, setApiError },
  );

  if (response.status === 200) {
    setAppointmentsData(response.data);
  }
};

/**
 * Retrieve a single appointment from the database.
 * @param {Function} setAppointmentData The function to collect the fetched data.
 * @param {number} id The appointment's id to fetch.
 * @param {Function} setIsLoading The function to update the loading status.
 * @param {Function} setApiError The function to update the api error status.
 * @returns {Promise<void>} A promise.
 */
export const getAppointmentById = async (
  setAppointmentData,
  id,
  setIsLoading,
  setApiError,
) => {
  const userId = KeycloakService.getUserId();
  const response = await request(
    () => HttpClient.getAxiosClient().get(`${USERS_ENDPOINT}/${userId}${APPOINTMENTS_ENDPOINT}/${id}`),
    { setIsLoading, setApiError },
  );

  if (response.status === 200) {
    setAppointmentData(response.data);
  }
};

/**
 * Delete an single appointment.
 * @param {number} id The targeted appointment identifier.
 * @param {Function} setState The function to update the flag status for the page refresh.
 * @param {Function} setIsLoading The function to update the loading status.
 * @param {Function} setApiError The function to update the api error status.
 */
export const deleteAppointment = async (
  id,
  setState,
  setIsLoading,
  setApiError,
) => {
  const response = await request(
    () => HttpClient.getAxiosClient().delete(`${APPOINTMENTS_ENDPOINT}/${id}`),
    { setIsLoading, setApiError },
  );

  if (response.status === 200 || response.status === 204) {
    CustomToast("Appointment successfully deleted", "success");
    setState?.((prev) => !prev);
  }
};

/**
 * Cancel an single appointment.
 * @param {number} id The targeted appointment identifier.
 * @param {Function} setState The function to update the flag status for the page refresh.
 * @param {Function} setIsLoading The function to update the loading status.
 * @param {Function} setApiError The function to update the api error status.
 * @returns {Promise<void>} A promise.
 */
export const cancelAppointment = async (
  id,
  setState,
  setIsLoading,
  setApiError,
) => {
  const response = await request(
    () => HttpClient.getAxiosClient().patch(`${APPOINTMENTS_ENDPOINT}/${id}/cancel`),
    { setIsLoading, setApiError },
  );

  if (response.status === 204) {
    CustomToast("Appointment successfully cancelled", "success");
    setState?.((prev) => !prev);
  }
};

/**
 * Delete multiple appointments at once.
 * @param {Array} ids A list of identifiers.
 * @param {Function} setState The function to update the flag status for the page refresh.
 * @param {Function} setIsLoading The function to update the loading status.
 * @param {Function} setApiError The function to update the api error status.
 */
export const deleteAppointments = async (
  ids,
  setState,
  setIsLoading,
  setApiError,
) => {
  const response = await request(
    () => HttpClient.getAxiosClient().delete(`${APPOINTMENTS_ENDPOINT}`, { data: ids }),
    { setIsLoading, setApiError },
  );

  if (response.status === 204) {
    CustomToast("Appointment successfully deleted", "success");
    setState?.((prev) => !prev);
  }
};

/**
 * Update an appointment in the database.
 * @param {Object} appointment The updated information of the targeted appointment.
 * @param {Function} setApiError The function to update the api error status.
 * @param {Function} setState The function to update the flag status for the page refresh.
 * @param {Function} setIsLoading The function to update the loading status.
 * @returns {Promise<void>} A promise.
 */
export const updateAppointment = async (
  appointment,
  setApiError,
  setState,
  setIsLoading,
) => {
  const response = await request(
    () => HttpClient.getAxiosClient().put(`${APPOINTMENTS_ENDPOINT}/${appointment.id}`, appointment),
    { setIsLoading, setApiError },
  );

  if (response.status === 200) {
    CustomToast("Appointment successfully updated", "success");
    setState?.((prev) => !prev);
  }

  return response.data;
};

/**
 * Update an appointment in the database.
 * @param {Object} appointment The updated information of the targeted appointment.
 * @param {Function} setApiError The function to update the api error status.
 * @param {Function} setState The function to update the flag status for the page refresh.
 * @param {Function} setIsLoading The function to update the loading status.
 * @returns {Promise<void>} A promise.
 */
export const createAppointment = async (
  appointment,
  setApiError,
  setState,
  setIsLoading,
) => {
  const response = await request(
    () => HttpClient.getAxiosClient().post(`${APPOINTMENTS_ENDPOINT}`, appointment),
    { setIsLoading, setApiError },
  );

  if (response.status === 201) {
    CustomToast("Appointment successfully created", "success");
    setState?.((prev) => !prev);
  }

  return response.data;
};

const getErrorStatus = (err) => err?.response?.status;

const getBackendErrorMessage = (err) => {
  const data = err?.response?.data;

  if (Array.isArray(data?.errors) && data.errors.length > 0) {
    return data.errors[0];
  }

  return data?.message || null;
};

const handleAppointmentRequestError = (err) => {
  const backendMessage = getBackendErrorMessage(err);

  switch (getErrorStatus(err)) {
    case 400:
      CustomToast(backendMessage || "Please review your data and try again.", "error");
      return;
    case 401:
      CustomToast("Please sign in to continue.", "error");
      return;
    case 403:
      CustomToast(backendMessage || "You are not allowed to perform this action.", "error");
      return;
    case 404:
      CustomToast(backendMessage || Errors.NOT_FOUND_ERROR, "info");
      return;
    case 409:
      CustomToast(backendMessage || Errors.CONFLICTING_SCHEDULE_ERROR, "error");
      return;
    case 503:
      CustomToast(Errors.SERVICE_UNAVAILABLE_ERROR, "error");
      return;
    default:
      showGenericRequestError(err);
  }
};

const showGenericRequestError = (err) => {
  if (err?.code === "ECONNABORTED") {
    CustomToast(Errors.REQUEST_TIMEOUT_ERROR, "error");
    return;
  }

  if (err?.response == null) {
    CustomToast(Errors.NETWORK_ERROR, "error");
    return;
  }

  const backendMessage = getBackendErrorMessage(err);
  CustomToast(backendMessage || Errors.API_ERROR, "error");
};
